import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createAccessHandbookLink,
  createHandbookLink,
  createHelpLink,
  CreateHelpLinkResponse,
  GETAccessHandbookLinks,
  GETHandbookLinks,
  GETHelpLinks,
  updateAccessHandbookLink,
  updateHandbookLink,
  updateHelpLink,
} from '@/queries/help_links/requests'

interface HelpLinksParams {
  globalOnId: number
  program_id?: number
  search?: string
}

const queryKey = ['admin_help_links']

export function useCreateHelpLink(
  options?: UseMutationOptions<
    CreateHelpLinkResponse,
    unknown,
    { program_id: number; link: string; globalOnId?: number }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ program_id, link, globalOnId = 1 }) =>
      createHelpLink(program_id, link, globalOnId),

    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.(...args)
    },

    onError: options?.onError,

    onSettled: options?.onSettled,
  })
}

export function useUpdateHelpLink(
  options?: UseMutationOptions<
    unknown,
    unknown,
    { id: number; data: { link: string; globalOnId?: number } }
  >
) {
  const queryClient = useQueryClient()
  const queryKey = ['admin_help_links']

  return useMutation({
    mutationFn: ({ id, data }) =>
      updateHelpLink(id, data.link, data.globalOnId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useHelpLinks(params: HelpLinksParams) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [...queryKey, params],
    queryFn: () => GETHelpLinks(params.globalOnId, params),
    refetchOnMount: 'always',
  })

  const invalidateQuery = () => {
    return queryClient.invalidateQueries({ queryKey: [...queryKey, params] })
  }

  return {
    query,
    invalidateQuery,
  }
}

export function useHandbookLinks(params: HelpLinksParams) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['providerHandbookLinks', params],
    queryFn: () =>
      GETHandbookLinks(params.globalOnId, { program_id: params.program_id }),
    refetchOnMount: 'always',
  })

  const invalidateQuery = () => {
    return queryClient.invalidateQueries({
      queryKey: ['providerHandbookLinks', params],
    })
  }

  return {
    query,
    invalidateQuery,
  }
}

export function useCreateProviderHandbookLink(
  options?: UseMutationOptions<
    CreateHelpLinkResponse,
    unknown,
    { program_id: number; link: string; globalOnId?: number }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ program_id, link, globalOnId = 1 }) =>
      createHandbookLink(program_id, link, globalOnId),

    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['providerHandbookLinks'] })
      options?.onSuccess?.(...args)
    },

    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useUpdateHandbookLink(
  options?: UseMutationOptions<
    unknown,
    unknown,
    { id: number; data: { link: string; globalOnId?: number } }
  >
) {
  const queryClient = useQueryClient()
  const queryKey = ['providerHandbookLinks']

  return useMutation({
    mutationFn: ({ id, data }) =>
      updateHandbookLink(id, data.link, data.globalOnId),

    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.(...args)
    },

    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useAccessHandbookLinks(params: HelpLinksParams) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['accessHandbookLinks', params], // Clave única para este query
    queryFn: () =>
      GETAccessHandbookLinks(params.globalOnId, {
        program_id: params.program_id,
      }),
    refetchOnMount: 'always',
  })

  const invalidateQuery = () => {
    return queryClient.invalidateQueries({
      queryKey: ['accessHandbookLinks', params],
    })
  }

  return {
    query,
    invalidateQuery,
  }
}

export function useCreateAccessHandbookLink(
  options?: UseMutationOptions<
    CreateHelpLinkResponse,
    unknown,
    { program_id: number; link: string; globalOnId?: number }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ program_id, link, globalOnId = 1 }) =>
      createAccessHandbookLink(program_id, link, globalOnId),

    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['accessHandbookLinks'] })

      options?.onSuccess?.(...args)
    },

    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useUpdateAccessHandbookLink(
  options?: UseMutationOptions<
    unknown,
    unknown,
    { id: number; data: { link: string; globalOnId?: number } }
  >
) {
  const queryClient = useQueryClient()
  const queryKey = ['accessHandbookLinks']

  return useMutation({
    mutationFn: ({ id, data }) =>
      updateAccessHandbookLink(id, data.link, data.globalOnId),

    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.(...args)
    },

    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}
